import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import CreateDsFromImageModal from 'components/Modal/CreateDsFromImageModal';
import CreateDsFromPromptModal from 'components/Modal/CreateDsFromPromptModal';
import DropdownMenu from 'components/Widget/DropdownMenu';
import { Tooltip } from 'components/Widget/Tooltip';
import { SceneMetadata } from 'dragonmouse-shared/interfaces';
import { Style } from 'dragonmouse-shared/style';
import { MenuItemAction, MenuItemDivider } from 'menu/MenuItem';
import React, { useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveScene, selectActiveStyle, selectIsFreeTier } from 'redux/db/reducers';
import { showContextMenu } from 'redux/mode/actions';
import { displayViewOnlyModal, displayPromptModal, displayUpgradeModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import ColorSetup from './ColorSetup';
import FontSetup from './FontSetup';
import InstructionSetup from './InstructionSetup';
import * as S from './styles';
import TokenSetup from './TokenSetup';
import TypeSetup from './TypeSetup';

enum Modals {
	PROMPT = 'createDsFromPrompt',
	IMAGE = 'createDsFromImage',
	TEMPLATE = 'loadDsTemplate',
}

const DesignSystem = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const [createMenuToolTipId] = useState(String(Math.random()));
	const [stylesMenuToolTipId] = useState(String(Math.random()));
	const [activeModal, setActiveModal] = useState<Modals | null>(null);

	const ref = useRef() as React.MutableRefObject<HTMLDivElement>;

	const { activeScene, activeStyle, isFreeTier } = useSelector((r: RootReducer) => {
		return {
			activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
			activeStyle: selectActiveStyle(r.db, r.mode.activeSceneId),
			isFreeTier: selectIsFreeTier(r.db),
		};
	}, shallowEqual);

	const availableStyles: Style[] = useMemo(() => {
		if (!activeScene?.metadata?.styles?.length) return [];
		return activeScene.metadata.styles;
	}, [activeScene?.metadata?.styles]);

	const currentStyle: number | null = useMemo(() => {
		return activeScene?.metadata?.currentStyle ?? null;
	}, [activeScene?.metadata?.currentStyle]);

	//------------------------------------------------------------------------------------------------
	// UTILITY FUNCTIONS
	//------------------------------------------------------------------------------------------------
	const updateSceneMetadata = (update: Partial<SceneMetadata>) => {
		if (!activeScene) return;
		forge.dbrUpdateScene({
			update: {
				id: activeScene.id,
				metadata: {
					...(activeScene.metadata as SceneMetadata),
					...update,
				},
			},
			showToast: true,
		});
	};

	const updateCurrentStyle = (update: Partial<Style>) => {
		if (!activeScene?.metadata || activeScene.metadata.currentStyle === null) return;
		updateSceneMetadata({
			styles: activeScene.metadata.styles.map((style, index) =>
				index === activeScene.metadata.currentStyle ? { ...style, ...update } : style
			),
		});
	};

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onNewStyle = (style?: Style) => {
		if (!activeScene) {
			console.error('Scene not active');
			return;
		}
		const newStyleIndex: number = availableStyles.length || 0;
		if (!style) {
			style = {
				title: 'Design System ' + (newStyleIndex + 1),
				instructions: '',
				fonts: [],
				colors: [],
				typography: [],
				tokens: [],
			};
		}
		updateSceneMetadata({
			styles: [...availableStyles, style],
			currentStyle: availableStyles.length || 0,
		});
	};

	// ask the user to rename the current style
	const onRenameStyle = () => {
		if (!activeStyle) return;

		displayPromptModal({
			title: 'Rename design system',
			description: 'Enter a new name for the design system',
			submitLabel: 'Rename',
			initialValue: activeStyle.title,
			onSubmit: async (value?: string) => {
				if (!value) return;
				updateCurrentStyle({ title: value });
			},
		});
	};

	const onDeleteStyle = () => {
		if (!activeScene || !activeStyle) {
			console.error('Scene not active');
			return;
		}
		updateSceneMetadata({
			styles: availableStyles.filter((_, index) => index !== currentStyle),
			currentStyle: null,
		});
	};

	const onHeaderContextMenu = (e: React.MouseEvent) => {
		if (!activeStyle) return;
		if (e.target instanceof HTMLElement) {
			dispatch(
				showContextMenu({
					point: { x: e.clientX, y: e.clientY },
					menuItems: headerMenuItems,
				})
			);
		}
	};

	//------------------------------------------------------------------------------------------------
	// MENU ITEMS
	//------------------------------------------------------------------------------------------------
	const headerMenuItems = useMemo(() => {
		if (!activeStyle) return [];
		return [
			new MenuItemAction('Rename...', '', onRenameStyle),
			new MenuItemAction('Delete', '', onDeleteStyle),
		];
	}, [activeStyle, onRenameStyle]);

	const createMenuItems = useMemo(() => {
		const handleCreateAction = (action: () => void) => {
			if (!!activeScene?.viewOnly) {
				displayViewOnlyModal();
				return;
			}
			if (isFreeTier) {
				displayUpgradeModal('Creating design systems');
				return;
			}
			action();
		};

		return [
			new MenuItemAction('Create DS from prompt...', '', () => {
				handleCreateAction(() => setActiveModal(Modals.PROMPT));
			}),
			new MenuItemAction('Create DS from image...', '', () => {
				handleCreateAction(() => setActiveModal(Modals.IMAGE));
			}),
			new MenuItemAction('Create blank DS', '', () => {
				handleCreateAction(() => onNewStyle());
			}),
		];
	}, [availableStyles, currentStyle, isFreeTier, activeScene]);

	// list all available styles, with an option to clear the current style
	// selecting will activate that style (or clear it)
	const stylesMenuItems = useMemo(() => {
		const sortedStyles = [...availableStyles].sort((a, b) => a.title.localeCompare(b.title));
		return [
			...sortedStyles.map((style) => {
				const isActive = availableStyles.indexOf(style) === currentStyle;
				return new MenuItemAction(
					style.title || '(unnamed)',
					'',
					() => {
						updateSceneMetadata({
							currentStyle: availableStyles.indexOf(style),
						});
					},
					false,
					isActive // showCheckmark
				);
			}),
			...(currentStyle !== null
				? [
						new MenuItemDivider(),
						new MenuItemAction('none', '', () => {
							updateSceneMetadata({
								currentStyle: null,
							});
						}),
				  ]
				: []),
		];
	}, [availableStyles, currentStyle]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderEmptyStateNoStyles = () => {
		return (
			<S.EmptyStateNoStyles>
				<S.Body>
					There are no design systems associated with this project. Click the + button above to add
					a design system.
				</S.Body>
			</S.EmptyStateNoStyles>
		);
	};

	const renderStyleSelector = () => {
		if (!availableStyles.length) return null;
		// const sortedStyles = availableStyles.sort((a, b) => a.title.localeCompare(b.title));

		return (
			<S.EmptyStateNoStyles>
				<S.Body>
					There is no active design system. Select a design system below or click the + button above
					to create a new one.
				</S.Body>
				<S.StyleEntries>
					{availableStyles.map((style: Style, index: number) => (
						<S.StyleEntry
							ref={ref}
							key={index}
							onMouseDown={(e) => {
								if (e.button !== 0) return; // Only handle left-clicks
								updateSceneMetadata({
									currentStyle: index,
								});
							}}
						>
							<S.StyleEntryText>{style.title}</S.StyleEntryText>
						</S.StyleEntry>
					))}
				</S.StyleEntries>
			</S.EmptyStateNoStyles>
		);
	};

	return (
		<>
			<S.Base ref={ref}>
				<S.Header onContextMenu={onHeaderContextMenu}>
					<S.Label empty={!activeStyle}>{activeStyle ? activeStyle.title : 'none'}</S.Label>
					<S.ButtonGroup>
						<DropdownMenu menuItems={createMenuItems}>
							<S.DropdownTrigger
								data-tooltip-id={createMenuToolTipId}
								data-tooltip-content="Create new design system"
							>
								<Icon type={IconType.ADD} width={16} height={16} color={theme.gray['1000']} />
							</S.DropdownTrigger>
							<Tooltip id={createMenuToolTipId} placement="top" />
						</DropdownMenu>
						{availableStyles.length > 0 && (
							<DropdownMenu menuItems={stylesMenuItems}>
								<S.DropdownTrigger
									data-tooltip-id={stylesMenuToolTipId}
									data-tooltip-content="Select a design system"
								>
									<Icon
										type={IconType.FILLED_ARROW_DOWN}
										width={16}
										height={16}
										color={theme.gray['1000']}
									/>
								</S.DropdownTrigger>
								<Tooltip id={stylesMenuToolTipId} placement="top" />
							</DropdownMenu>
						)}
					</S.ButtonGroup>
				</S.Header>
				{activeStyle ? (
					<>
						<FontSetup
							activeStyle={activeStyle}
							updateCurrentStyle={updateCurrentStyle}
							viewOnly={!!activeScene?.viewOnly}
						/>
						<TypeSetup
							activeStyle={activeStyle}
							updateCurrentStyle={updateCurrentStyle}
							viewOnly={!!activeScene?.viewOnly}
						/>
						<ColorSetup
							activeStyle={activeStyle}
							updateCurrentStyle={updateCurrentStyle}
							viewOnly={!!activeScene?.viewOnly}
						/>
						<TokenSetup
							activeStyle={activeStyle}
							updateCurrentStyle={updateCurrentStyle}
							viewOnly={!!activeScene?.viewOnly}
						/>
						<InstructionSetup
							activeStyle={activeStyle}
							updateCurrentStyle={updateCurrentStyle}
							viewOnly={!!activeScene?.viewOnly}
						/>
					</>
				) : availableStyles.length === 0 ? (
					renderEmptyStateNoStyles()
				) : (
					renderStyleSelector()
				)}
			</S.Base>
			{activeModal === Modals.PROMPT && (
				<CreateDsFromPromptModal
					close={() => setActiveModal(null)}
					onStyleCreated={(style: Style) => {
						onNewStyle(style);
						setActiveModal(null);
					}}
				/>
			)}
			{activeModal === Modals.IMAGE && (
				<CreateDsFromImageModal
					close={() => setActiveModal(null)}
					onStyleCreated={(style: Style) => {
						onNewStyle(style);
						setActiveModal(null);
					}}
				/>
			)}
		</>
	);
};

export default DesignSystem;
